import React, { useEffect, useState } from "react";
import { FaClock } from "react-icons/fa";
import { formatQuestTime, getUpcomingQuestDeadline } from "../utils/questUtils";

const ONE_HOUR_MS = 60 * 60 * 1000;

const buildMidnightTarget = (referenceDate) => {
  const midnight = new Date(referenceDate);
  midnight.setHours(24, 0, 0, 0);
  return midnight;
};

const formatCountdown = (diffMs) => {
  if (diffMs <= 0) return "00:00:00";

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

const QuestTimer = ({ quests, questDate = null, wakeTime = null }) => {
  const [timerState, setTimerState] = useState({
    countdown: "00:00:00",
    heading: "Day reset",
    detail: "Complete pending quests before midnight.",
    tooltip: "Pending quests convert into consequences after the day closes.",
    isWarning: false,
  });
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const nextDeadline = getUpcomingQuestDeadline(quests, now, questDate, wakeTime);

      const targetDate = nextDeadline?.targetDate || buildMidnightTarget(now);
      const diff = targetDate.getTime() - now.getTime();
      const isWarning = diff <= ONE_HOUR_MS;

      if (nextDeadline) {
        const cutoff = formatQuestTime(nextDeadline.quest.deadlineTime);
        setTimerState({
          countdown: formatCountdown(diff),
          heading: `Next cut-off: ${cutoff}`,
          detail: nextDeadline.quest.title,
          tooltip: `If "${nextDeadline.quest.title}" is not completed by ${cutoff}, it fails automatically.`,
          isWarning,
        });
        return;
      }

      setTimerState({
        countdown: formatCountdown(diff),
        heading: "Day reset",
        detail: "Complete pending quests before midnight.",
        tooltip: isWarning
          ? "Less than one hour remains before the daily board closes."
          : "Pending quests that survive until midnight will be processed by the System.",
        isWarning,
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [questDate, quests, wakeTime]);

  if (!quests || quests.length === 0) {
    return null;
  }

  const hasActiveQuests = quests.some(
    (quest) => !quest.completed && !quest.claimed && quest.status !== "failed"
  );

  if (!hasActiveQuests) {
    return null;
  }

  return (
    <div
      className={`quest-timer ${timerState.isWarning ? "warning" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr) auto",
        alignItems: "center",
        gap: "12px",
        padding: "14px 16px",
        marginBottom: "16px",
        backgroundColor: timerState.isWarning ? "#2a1a1a" : "#1a1d2a",
        borderRadius: "14px",
        border: `2px solid ${timerState.isWarning ? "#ff4848" : "#42aaff"}`,
        boxShadow: timerState.isWarning
          ? "0 0 20px rgba(255, 72, 72, 0.4)"
          : "0 0 20px rgba(66, 170, 255, 0.3)",
        color: timerState.isWarning ? "#ff7676" : "#42aaff",
        cursor: "pointer",
        transition: "all 0.3s ease",
      }}
    >
      <FaClock style={{ fontSize: "1.25em" }} />
      <div className="quest-timer__copy">
        <div className="quest-timer__eyebrow">{timerState.heading}</div>
        <div className="quest-timer__label">{timerState.detail}</div>
      </div>
      <div className="quest-timer__countdown">{timerState.countdown}</div>

      {isHovered && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(92vw, 420px)",
            backgroundColor: timerState.isWarning ? "#4a2a2a" : "#16324a",
            color: "#ffffff",
            padding: "10px 12px",
            borderRadius: "10px",
            fontSize: "0.9em",
            lineHeight: 1.45,
            zIndex: 1000,
            border: `1px solid ${timerState.isWarning ? "#ff4848" : "#42aaff"}`,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            textAlign: "center",
          }}
        >
          {timerState.tooltip}
        </div>
      )}
    </div>
  );
};

export default QuestTimer;
